import { IDENTITY_VERIFICATION_STATUSES_REQUIRING_ACTION } from "@/api/user/userTypes";
import DoneCheck from "@/components/Icons/DoneCheck";
import LongRightArrow from "@/components/Icons/LongRightArrow";
import Selfie from "@/components/Icons/Selfie";
import StripeLogo from "@/components/Icons/StripeLogo";
import VerifiedIdentification from "@/components/Icons/VerifiedIdentification";
import { PageOutletContext } from "@/components/Page/PageOutlet";
import { format } from "@/utils/date";
import { Button } from "@industriousoffice/react-components";
import { Trans, useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";

interface VerificationRequiredBannerProps {
  timezone?: string;
}

export function VerificationRequiredBanner({
  timezone,
}: VerificationRequiredBannerProps) {
  const { t } = useTranslation("dashboard");

  const { currentUser } = useOutletContext<PageOutletContext>();

  if (currentUser == null) {
    return null;
  }

  const requiresVerification = currentUser.identityVerifications?.some(
    (verification) =>
      IDENTITY_VERIFICATION_STATUSES_REQUIRING_ACTION.includes(
        verification.status
      )
  );

  if (!requiresVerification) {
    return null;
  }

  const soonestDeadline = currentUser.identityVerifications
    ?.filter((verification) =>
      IDENTITY_VERIFICATION_STATUSES_REQUIRING_ACTION.includes(
        verification.status
      )
    )
    .sort(
      (a, b) =>
        new Date(a.deadlineDateIso).getTime() -
        new Date(b.deadlineDateIso).getTime()
    )[0];

  const goToVerificationPage = () => {
    const verificationPageUrl = new URL(
      `/identity-verification`,
      import.meta.env.VITE_WEBSITE_URL
    ).toString();
    window.open(verificationPageUrl, "_blank");
  };
  return (
    <div className="bg-almond-99 p-8 rounded-lg flex flex-col gap-4">
      <div className="font-bold flex justify-between">
        <div className="flex items-center">
          <VerifiedIdentification className="mr-4" />
          {t("Identity Verification")}
        </div>
        <StripeLogo />
      </div>
      <div className="text-md">
        <Trans
          t={t}
          components={{
            bold: <strong />,
            br: <br />,
            italic: <em />,
          }}
        >
          {t("To unlock full access to the Member Portal ", {
            verifyBy: soonestDeadline
              ? format(
                  new Date(soonestDeadline.deadlineDateIso),
                  "MM/dd",
                  timezone
                )
              : "",
          })}
        </Trans>
      </div>
      <div className="flex gap-4 mx-auto items-center font-bold text-grey-25 mt-4">
        <div className="flex flex-col items-center gap-1 md:w-[120px]">
          <VerifiedIdentification />
          {t("Upload photo ID")}
        </div>
        <LongRightArrow />
        <div className="flex flex-col items-center gap-1 md:w-[120px]">
          <Selfie />
          {t("Take a selfie")}
        </div>
        <LongRightArrow />
        <div className="flex flex-col items-center gap-1  md:w-[120px]">
          <DoneCheck />
          {t("Done!")}
        </div>
      </div>
      <Button className="max-w-[150px] mx-auto" onClick={goToVerificationPage}>
        {t("Verify Now")}
      </Button>
    </div>
  );
}
